import { html, View } from 'rune-ts';
import { ProductListApiS } from '../../../../../features/ProductList/outbound/share';
import {
  ProductCardListView,
  ProductCardListOption,
} from '../../../../components/cells/ProductCardList/ProductCardListView.ts';
import klass from './CategoryProductRankingView.module.scss';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle';
import {
  bestProductTypeMap,
  ProductCardViewData,
  ProductCardSize,
} from '@services/MarppleShop/features/ProductList/type';
import { CateListTabsController } from '@services/MarppleShop/renderApp/pages/ProductRanking/utils/CateListTabsController.ts';
import { SeeMoreLinkView } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/views/SeeMoreLinkView/SeeMoreLinkView.ts';
import { Tabs } from '@services/MarppleShop/renderApp/components/cells/Tabs/Tabs.ts';
import { TabChangeEvent } from '@services/MarppleShop/renderApp/components/cells/Tabs/events.ts';
import { createGoodsUrlBuilder } from '@services/MarppleShop/features/ProductList/util';
import { ICategory } from '../../../../../../../shared/mps-shared/domain/category/types.ts';
import { DefaultProductListEmptyScreen } from '@services/MarppleShop/renderApp/components/cells/ProductCardList/ProductCardListEmptyScreen.ts';
import { PostMessageNavigate } from '@services/MarppleShop/shared/app/navigate.ts';

export type CategoryProductRankingViewData = {
  readonly section_title: {
    title: string;
    sub_title?: string;
    see_more_link: string;
    navigate?: PostMessageNavigate;
  };
  products: ProductCardViewData[];

  /**
   * 없으면 안 그립니다
   */
  readonly cate_lists?: ICategory[];

  readonly adult_check_needed: boolean;

  readonly hide_tooltip?: boolean;

  readonly query?: {
    cate_list_id?: number;
  };

  readonly goods_cate_root_id: number;
};

interface CategoryProductRankingViewOption {
  is_mobile: boolean;
}

export class CategoryProductRankingView extends View<CategoryProductRankingViewData> {
  private seeMoreLink: string;

  private readonly cardListOptions: ProductCardListOption;

  private readonly firstCardList: ProductCardListView;
  private readonly secondCardList: ProductCardListView;

  private readonly sectionTitle: SectionTitle;

  private readonly tabsView: Tabs | null = null;

  constructor(data: CategoryProductRankingViewData, option: CategoryProductRankingViewOption) {
    super(data, option);
    this.seeMoreLink = this.data.section_title.see_more_link;

    this.cardListOptions = option.is_mobile
      ? {
          resize_width: 400,
          size: ProductCardSize.small,
          container_klass: klass.product_list,
          is_horizontal: true,
          is_lazy: true,
          grid: {
            rows: 5,
            row_gap: 16,
          },
          limit: 5,
          timer: {
            enabled: false,
          },
        }
      : {
          size: ProductCardSize.large,
          resize_width: 600,
          container_klass: klass.product_list,
          is_lazy: true,
          grid: {
            columns: 5,
            column_gap: 16,
          },
          limit: 5,
          timer: {
            enabled: false,
          },
        };

    const firstGroupCardItems = this.data.products.slice(0, 5);
    const secondGroupCardItems = this.data.products.slice(5);

    this.firstCardList = new ProductCardListView(
      { products: firstGroupCardItems },
      this.cardListOptions,
    ).setEmptyListView(
      new DefaultProductListEmptyScreen({
        title: ET('mps2::main::category_product_ranking::empty_title'),
        body: option.is_mobile
          ? ET('mps2::main::category_product_ranking::empty_description_mo')
          : ET('mps2::main::category_product_ranking::empty_description_pc'),
      }),
    );

    this.secondCardList = new ProductCardListView(
      { products: secondGroupCardItems },
      this.cardListOptions,
    ).setEmptyListView(null);

    this.sectionTitle = new SectionTitle({
      title_props: {
        ...this.data.section_title,
        size: option.is_mobile ? 'medium' : 'xLarge',
        // tooltip:
        //   this.data.hide_tooltip && visbile
        //     ? undefined
        //     : new Tooltip({
        //         id: `${this}ToolTip`,
        //         size: option.is_mobile ? 'small' : 'medium',
        //         content: html`
        //           <div class="${klass.tooltip}">
        //             ${html.preventEscape(
        //               UtilS.escape(ET('mps2::main::category_product_ranking::tooltip'), true),
        //             )}
        //           </div>
        //         `,
        //       }),
      },
      // plugins: [
      //   new SeeMoreLinkPlugin({
      //     see_more_link: this.seeMoreLink,
      //     size: option.is_mobile ? 'xSmall' : 'medium',
      //     theme: 'light',
      //   }),
      // ],
    });

    if (this.data.cate_lists) {
      this.tabsView = new Tabs({
        tabs: CateListTabsController.getTabItems(this.data.cate_lists),
        custom_klass: klass.catelist_tabs_wrap,
        current_tab_id: this.data.query?.cate_list_id ? this.data.query.cate_list_id.toString() : '',
      });
    }
  }

  protected override onRender() {
    this.delegate(TabChangeEvent, Tabs, async (e) => {
      const cate_list_id = e.detail.tabId ? Number(e.detail.tabId) : undefined;

      const { products } = await ProductListApiS.getBestProductListApi({
        cate_root_id: this.data.goods_cate_root_id,
        cate_list_id,
        limit: 10,
        exclude_sold_out: true,
        rank_type: bestProductTypeMap.weekly,
      });

      this.data.products = products;

      const firstGroupCardItems = products.slice(0, 5);
      const secondGroupCardItems = products.slice(5);

      this.firstCardList.setProductsData(firstGroupCardItems).redraw();
      this.secondCardList.setProductsData(secondGroupCardItems).redraw();

      // 더보기 링크 업데이트
      this.seeMoreLink = cate_list_id
        ? createGoodsUrlBuilder()
            .setPage('PRODUCTS')
            .setFilters({ cate_list_id, without_sold_out: true })
            .build()
        : createGoodsUrlBuilder().build();

      const see_more_link_view = this.sectionTitle.pluginViews.find((view) => {
        return view instanceof SeeMoreLinkView;
      });

      if (see_more_link_view) {
        see_more_link_view.seeMoreLink = this.seeMoreLink;
      }

      const container_el = this.element().querySelector(`.${klass.product_list_container}`);
      const wrap_el = this.element().querySelector(`.${klass.product_list_wrap}`);

      if (this.isFirstGroupVisible) {
        if (container_el) {
          container_el.classList.remove(klass.empty);
        }
        if (wrap_el) {
          wrap_el.classList.remove(klass.empty);
        }
      } else {
        if (container_el) {
          container_el.classList.add(klass.empty);
        }
        if (wrap_el) {
          wrap_el.classList.add(klass.empty);
        }
      }
    });
  }

  private get isFirstGroupVisible(): boolean {
    return this.data.products.length > 0;
  }

  protected override template() {
    return html`
      <section class="${klass.container}">
        <!-- Section Title -->
        <div class="${klass.title_wrap}">${this.sectionTitle}</div>

        <!-- 대 카테고리 -->
        ${this.tabsView ? html`<div class="${klass.category_tabs}">${this.tabsView}</div> ` : ''}

        <!-- 상품 목록 -->
        <div class="${klass.product_list_wrap}">
          <div class="${klass.product_list_container} ${this.isFirstGroupVisible ? '' : klass.empty}">
            ${this.firstCardList} ${this.secondCardList}
          </div>
        </div>
      </section>
    `;
  }
}
